import { z } from "zod";
import {
  GRANTABLE_ROLES,
  STAFF_PERMISSIONS,
  employeeRoleSchema,
} from "./roles";

// TES-491 — staff administration. The admin role's one exclusive power: every
// other admin surface (content, flows, imports) is shared with the doctor for
// compatibility, but who works here and in what role is admin-only.
//
// Deliberately NOT on ClinicianController: that controller is patient-scoped
// (CareRelationshipGuard resolves `:id` as a patient and writes an access-log
// row). A staff id in that position would either be logged as a patient access
// or 404 — neither is a sane way to edit a colleague.

// A staff member as the admin list renders them. No personal number: the admin
// list is a roster, not an identity register, and the PN is field-encrypted at
// rest (TES-388). `hasPersonalNumber` is what the admin actually needs — it
// answers "can this person sign in with BankID yet?", which is the only
// question the roster has to settle.
export const staffMemberSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  email: z.string(),
  role: employeeRoleSchema,
  // TES-1089 — roles granted on top of `role` (only `admin` today). Defaults
  // to none so a roster from an older API deployment still parses.
  grantedRoles: z.array(z.enum(GRANTABLE_ROLES)).default([]),
  // TES-1372 — purpose-bound permissions (only `insights` today). Defaults to
  // none so a roster from an older API deployment still parses.
  permissions: z.array(z.enum(STAFF_PERMISSIONS)).default([]),
  hasPersonalNumber: z.boolean(),
  deactivatedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type StaffMember = z.infer<typeof staffMemberSchema>;

export const staffListResponseSchema = z.object({
  rows: z.array(staffMemberSchema),
});
export type StaffListResponse = z.infer<typeof staffListResponseSchema>;

// Creating a staff member. Same PN rules as the CAR-55 clinician form (12
// digits, plausible birth year) — BankID enforces the checksum at sign-in, and
// a typo surfaces there rather than admitting the wrong person.
export const createStaffBodySchema = z.object({
  displayName: z.string().min(1).max(120).trim(),
  email: z.string().email().max(254).toLowerCase().trim(),
  personalNumber: z
    .string()
    .regex(/^\d{12}$/, { message: "must be 12 digits, no separator" })
    .refine(
      (s) => {
        const year = Number(s.slice(0, 4));
        return year >= 1900 && year <= new Date().getFullYear();
      },
      { message: "implausible birth year" },
    ),
  // TES-491: the role is now chosen, not assumed. The old "Ny läkare" form
  // had no role field and the service defaulted the column to 'doctor', so
  // the portal could not create a coach at all.
  role: employeeRoleSchema,
});
export type CreateStaffBody = z.infer<typeof createStaffBodySchema>;

// Updating one. Both fields optional, at least one required — a PATCH that
// changes nothing is a caller bug worth surfacing rather than a silent 200.
export const updateStaffBodySchema = z
  .object({
    role: employeeRoleSchema.optional(),
    // true = deactivate, false = reactivate. Not a timestamp: the client
    // should never get to choose when someone lost access.
    deactivated: z.boolean().optional(),
    // TES-1089 — true = grant `admin` on top of the base role, false = revoke
    // it. Refused for a doctor, and refused when revoking her own grant would
    // leave the clinic without an admin.
    adminGrant: z.boolean().optional(),
    // TES-1372 — true = grant the "Insikter" permission, false = revoke it.
    // Any staff role may hold it; no role implies it.
    insightsGrant: z.boolean().optional(),
    // TES-1400 (Ö-541) — true = grant "Annat (avvikande)" in the medication
    // catalogue, false = revoke it. Only a doctor orders, so only a doctor
    // may hold it.
    deviatingMedicationGrant: z.boolean().optional(),
  })
  .refine(
    (b) =>
      b.role !== undefined ||
      b.deactivated !== undefined ||
      b.adminGrant !== undefined ||
      b.insightsGrant !== undefined ||
      b.deviatingMedicationGrant !== undefined,
    { message: "nothing to update" },
  );
export type UpdateStaffBody = z.infer<typeof updateStaffBodySchema>;
